import { memo, useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

import { useKanbanContext } from './context';
import { kanbanTunnel } from './overlay-tunnel';
import type { KanbanCardProps, KanbanItemProps } from './types';

// Memoized Kanban card - prevents re-renders during list updates
function KanbanCardInner<T extends KanbanItemProps = KanbanItemProps>(
  props: KanbanCardProps<T>
) {
  const { id, name, children, className } = props;
  const { attributes, listeners, setNodeRef, transition, transform, isDragging } =
    useSortable({ id });
  const { activeCardId } = useKanbanContext();

  // Memoize style object to prevent object recreation
  const style = useMemo(() => ({
    transition,
    transform: CSS.Transform.toString(transform),
  }), [transition, transform]);

  // Memoize content to prevent recreation
  const content = useMemo(() =>
    children ?? <p className="m-0 text-sm font-medium">{name}</p>,
    [children, name]
  );

  // Memoize class names
  const cardClassName = useMemo(() => cn(
    'cursor-grab gap-4 rounded-md p-3 shadow-sm',
    isDragging && 'pointer-events-none cursor-grabbing opacity-30',
    className
  ), [isDragging, className]);

  const overlayClassName = useMemo(() => cn(
    'cursor-grab gap-4 rounded-md p-3 shadow-sm ring-2 ring-primary',
    isDragging && 'cursor-grabbing',
    className
  ), [isDragging, className]);

  return (
    <>
      <div style={style} {...listeners} {...attributes} ref={setNodeRef}>
        <Card className={cardClassName}>
          {content}
        </Card>
      </div>
      {activeCardId === id && (
        <kanbanTunnel.In>
          <Card className={overlayClassName}>
            {content}
          </Card>
        </kanbanTunnel.In>
      )}
    </>
  );
}

// Export memoized version for performance in lists
export const KanbanCard = memo(KanbanCardInner) as typeof KanbanCardInner;
